import { Builder } from '@builder.io/react';
import dynamic from 'next/dynamic';
import {
	TEXT_ALIGNMENT_OPTIONS,
	TextAlignmentOptions,
	BG_IMAGE_ALIGNMENT_OPTIONS,
	BgImageAlignmentOptions,
	Icon,
} from '#const';
import { getIconUrl } from '#utils';

const HeroWithImage = dynamic(() => import('./HeroWithImage'));

const DEFAULT_BACKGROUND_IMAGE = {
	src: '/ColumnBackground.webp',
	backgroundImagePlacement: BgImageAlignmentOptions.CENTER,
	backgroundOpacity: 1,
};

const DEFAULT_FOREGROUND_IMAGE = {
	src: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=500&h=700&fit=crop',
	alt: 'Professional portrait',
};

export const defaultOptions = {
	heading: '<h1><strong>$800+ MILLION <span style="color:#FFD700;">RECOVERED</span></strong></h1>',
	copy: '<p>For Our California Car Accident<br/>& Personal Injury Clients</p>',
	lowerCopy: '<p><em><strong>You pay nothing</strong> unless we win your case.</em></p>',
	foregroundImage: DEFAULT_FOREGROUND_IMAGE,
	backgroundImage: DEFAULT_BACKGROUND_IMAGE,
	mobileHeading:
		'<h1><strong>$800+ MILLION <span style="color:#FFD700;">RECOVERED</span></strong></h1>',
	mobileCopy: '<p>For Our California Car Accident<br/>& Personal Injury Clients</p>',
	mobileCopyAlignment: TextAlignmentOptions.LEFT,
	mobileLowerCopy: '<p><em><strong>You pay nothing</strong> unless we win your case.</em></p>',
	mobileForegroundImage: DEFAULT_FOREGROUND_IMAGE,
	mobileBackgroundImage: DEFAULT_BACKGROUND_IMAGE,
} as const;

Builder.registerComponent(HeroWithImage, {
	name: 'HeroWithImage',
	friendlyName: 'Hero With Image',
	inputs: [
		{
			name: 'heading',
			friendlyName: 'Desktop Heading',
			type: 'richText',
			required: true,
			defaultValue: defaultOptions.heading,
		},
		{
			name: 'copy',
			friendlyName: 'Desktop Text',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.copy,
		},
		{
			name: 'lowerCopy',
			friendlyName: 'Desktop Lower Text',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.lowerCopy,
		},
		{
			name: 'foregroundImage',
			friendlyName: 'Desktop Foreground Image',
			type: 'object',
			required: false,
			helperText: 'Large image placed beside the text, flush against the form panel on desktop.',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'alt',
					friendlyName: 'Alt Text',
					type: 'string',
					helperText: 'Briefly describe the image for accessibility.',
				},
			],
		},
		{
			name: 'backgroundImage',
			friendlyName: 'Desktop Background Image',
			type: 'object',
			required: false,
			helperText: 'Image placed behind the entire hero section.',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'backgroundImagePlacement',
					friendlyName: 'Background Image Placement Bias',
					type: 'string',
					enum: [...BG_IMAGE_ALIGNMENT_OPTIONS],
					defaultValue: BgImageAlignmentOptions.CENTER,
					helperText: 'How the background image will attach. Use to adjust the focal point.',
				},
				{
					name: 'backgroundOpacity',
					friendlyName: 'Background Opacity',
					type: 'number',
					defaultValue: 1,
					min: 0,
					max: 1,
					step: 0.05,
					helperText: 'Opacity of the background image, from 0 to 1.',
				},
			],
		},
		{
			name: 'mobileHeading',
			friendlyName: 'Mobile Heading',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.mobileHeading,
			helperText: 'Falls back to the desktop heading if left empty.',
		},
		{
			name: 'mobileCopy',
			friendlyName: 'Mobile Text',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.mobileCopy,
			helperText: 'Falls back to the desktop text if left empty.',
		},
		{
			name: 'mobileCopyAlignment',
			friendlyName: 'Mobile Text Alignment',
			type: 'string',
			enum: [...TEXT_ALIGNMENT_OPTIONS],
			defaultValue: defaultOptions.mobileCopyAlignment,
		},
		{
			name: 'mobileLowerCopy',
			friendlyName: 'Mobile Lower Text',
			type: 'richText',
			required: false,
			defaultValue: defaultOptions.mobileLowerCopy,
			helperText: 'Falls back to the desktop lower text if left empty.',
		},
		{
			name: 'mobileForegroundImage',
			friendlyName: 'Mobile Foreground Image',
			type: 'object',
			required: false,
			helperText:
				'Shown below text on mobile. Falls back to the desktop foreground image if left empty.',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'alt',
					friendlyName: 'Alt Text',
					type: 'string',
					helperText: 'Briefly describe the image for accessibility.',
				},
			],
		},
		{
			name: 'mobileBackgroundImage',
			friendlyName: 'Mobile Background Image',
			type: 'object',
			required: false,
			helperText: 'Background image for mobile. If empty, no mobile background is shown.',
			subFields: [
				{
					name: 'src',
					friendlyName: 'Image',
					type: 'file',
					allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
				},
				{
					name: 'backgroundImagePlacement',
					friendlyName: 'Background Image Placement Bias',
					type: 'string',
					enum: [...BG_IMAGE_ALIGNMENT_OPTIONS],
					defaultValue: BgImageAlignmentOptions.CENTER,
					helperText: 'How the background image will attach. Use to adjust the focal point.',
				},
				{
					name: 'backgroundOpacity',
					friendlyName: 'Mobile Background Opacity',
					type: 'number',
					defaultValue: 1,
					min: 0,
					max: 1,
					step: 0.05,
					helperText: 'Opacity of the background image on mobile, from 0 to 1.',
				},
			],
		},
	],
	image: getIconUrl(Icon.HERO),
});
